/**
 * Express Entry → one file: profile, indicative CRS estimate (with the rule table used),
 * personalised checklist with private uploads, pre-submission checks, client notice,
 * validation by a designated validator, submission recorded by the agency (D-067, D-068).
 */

import {
  ApiError,
  type Dossier,
  type DossierItem,
  type DossierItemStatusEnum,
  useImmigrationDossiersAcceptNoticeCreate,
  useImmigrationDossiersChecklistCreate,
  useImmigrationDossiersChecklistList,
  useImmigrationDossiersChecksRetrieve,
  useImmigrationDossiersDrawsRetrieve,
  useImmigrationDossiersFormsRetrieve,
  useImmigrationDossiersProvincesRetrieve,
  useImmigrationInterestsCreate,
  useImmigrationInterestsList,
  useImmigrationInterestsPartialUpdate,
  useImmigrationDossiersLeversRetrieve,
  useImmigrationDossiersPartialUpdate,
  useImmigrationDossiersRetrieve,
  useImmigrationDossiersSubmittedCreate,
  useImmigrationDossiersValidateCreate,
  useImmigrationItemsPartialUpdate,
  useImmigrationItemsUploadCreate,
  useImmigrationSettingsList,
  useCrmMembersList,
  useImmigrationDossiersClientAccessCreate,
  useImmigrationDossiersClientAccessDestroy,
  useImmigrationDossiersClientAccessRetrieve,
  useImmigrationDossiersClientMessagesCreate,
  useImmigrationDossiersClientMessagesList,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, SelectField, Skeleton, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { Page } from "../components/AppShell";
import { localized } from "../components/TranslatedFields";
import { fieldErrors, useCurrentRole } from "../lib/settings";
import { OfficerReview } from "./dossier/OfficerReview";
import { PassportReading } from "./dossier/PassportReading";
import { ProfileForm } from "./dossier/ProfileForm";
import { Strategy } from "./dossier/Strategy";
import { Synthesis } from "./dossier/Synthesis";

const ITEM_STATUSES: DossierItemStatusEnum[] = ["to_request", "received", "verified", "to_redo", "not_applicable"];

type Json = Record<string, unknown>;

function useRefreshDossier() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/immigration/"),
    });
}

function WhatIf({ id }: { id: string }) {
  const { t } = useTranslation();
  const levers = useImmigrationDossiersLeversRetrieve(id, { query: { staleTime: 0 } });
  if (levers.data?.status !== 200) return null;
  const list = (levers.data.data as { levers: { lever: string; points: number; new_total: number; cost: number | null }[] }).levers;
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("ee.whatIf")}</h2>
      <p role="note" className="muted">{t("ee.indicative")}</p>
      {list.length ? (
        <ol className="stack">
          {list.map((lever) => (
            <li key={lever.lever}>
              <strong>{t(`ee.lever.${lever.lever}`)}</strong> · {t("ee.points", { points: lever.points, total: lever.new_total })}
              {lever.cost == null ? <span className="muted"> · {t("ee.costUnknown")}</span> : null}
            </li>
          ))}
        </ol>
      ) : (
        <p className="muted">{t("ee.noLever")}</p>
      )}
    </GlassCard>
  );
}

function memberLabel(t: (k: string, o?: Record<string, unknown>) => string, member: string): string {
  if (member.startsWith("child")) return t("ee.child", { n: member.slice(5) });
  return t(`ee.members.${member}`);
}

function ItemRow({ item }: { item: DossierItem }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshDossier();
  const update = useImmigrationItemsPartialUpdate({ mutation: { onSuccess: refresh } });
  const upload = useImmigrationItemsUploadCreate({ mutation: { onSuccess: refresh } });
  const input = useRef<HTMLInputElement>(null);
  return (
    <li className={`doc-item doc-item--${item.status ?? "to_request"} stack`}>
      <div className="row between">
        <strong>
          {localized(item.title, i18n.language)} <span className="muted doc-item__member">· {memberLabel(t, item.member)}</span>
        </strong>
        <Badge tone={item.status === "verified" ? "success" : item.status === "to_redo" ? "danger" : "default"}>
          {t(`ee.itemStatus.${item.status}`)}
        </Badge>
      </div>
      <p className="muted" lang={i18n.language}>
        {localized(item.guidance, i18n.language)}
      </p>
      <div className="grid-2">
        <SelectField
          label={t("ee.statusLabel")}
          value={item.status ?? "to_request"}
          options={ITEM_STATUSES.map((s) => ({ value: s, label: t(`ee.itemStatus.${s}`) }))}
          onChange={(e) => update.mutate({ id: item.id, data: { status: e.target.value as DossierItemStatusEnum } })}
        />
        <TextField
          label={t("ee.expires")}
          type="date"
          value={item.expires_on ?? ""}
          onChange={(e) => update.mutate({ id: item.id, data: { expires_on: e.target.value || null } })}
        />
      </div>
      <div className="row">
        <input
          ref={input}
          type="file"
          className="visually-hidden"
          accept="application/pdf,image/*"
          aria-label={t("ee.upload")}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate({ id: item.id, data: { file } });
            e.target.value = "";
          }}
        />
        <Button variant="secondary" disabled={upload.isPending} onClick={() => input.current?.click()}>
          {upload.isPending ? t("common.loading") : t("ee.upload")}
        </Button>
        {item.file_url ? (
          <a className="link" href={item.file_url} target="_blank" rel="noreferrer">
            {t("ee.open")}
          </a>
        ) : null}
      </div>
      {item.rule_key === "passport" && item.file_url ? <PassportReading item={item} /> : null}
    </li>
  );
}

function Checklist({ id }: { id: string }) {
  const { t } = useTranslation();
  const refresh = useRefreshDossier();
  // While the AI reads a passport (P4-03), the list is fetched again every 3 s.
  const items = useImmigrationDossiersChecklistList(id, {
    query: {
      refetchInterval: (query) => {
        const response = query.state.data;
        const reading = response?.status === 200 && response.data.some((i) => i.extraction_status === "pending");
        return reading ? 3000 : false;
      },
    },
  });
  const generate = useImmigrationDossiersChecklistCreate({ mutation: { onSuccess: refresh } });
  const list = items.data?.status === 200 ? items.data.data : [];
  const verified = list.filter((i) => i.status === "verified" || i.status === "not_applicable").length;
  return (
    <GlassCard as="section" className="stack">
      <div className="row between">
        <div className="checklist-head">
          <h2>{t("ee.checklist")}</h2>
          {list.length ? (
            <div className="meter checklist-head__meter">
              <div className="meter__bar" role="presentation">
                <div className="meter__fill meter__fill--ok" style={{ transform: `scaleX(${verified / list.length})` }} />
              </div>
              <span className="check__detail ao-num">{t("ee.checklistProgress", { done: verified, total: list.length })}</span>
            </div>
          ) : null}
        </div>
        <Button variant="secondary" disabled={generate.isPending} onClick={() => generate.mutate({ id })}>
          {t("ee.generate")}
        </Button>
      </div>
      {items.isPending ? <Skeleton style={{ height: 180 }} /> : null}
      <ul className="plain-list doc-grid">
        {list.map((item) => (
          <ItemRow key={item.id} item={item} />
        ))}
      </ul>
    </GlassCard>
  );
}

type Text = Record<string, string>;
interface GuideField {
  label: Text;
  guidance: Text;
  critical: boolean;
  value: string | number | boolean | null;
}
interface GuideForm {
  key: string;
  code: string;
  title: Text;
  where: Text;
  members: string[];
  sections: { title: Text; fields: GuideField[] }[];
}
interface Guide {
  general: Text[];
  forms: GuideForm[];
  table_status: string;
  table_version: number;
  table_source: string;
}

/** EE-5: which IRCC forms this client needs and how to fill each answer (data "to verify"). */
function FormsGuide({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const guide = useImmigrationDossiersFormsRetrieve(id, { query: { staleTime: 0 } });
  if (guide.isPending) return <Skeleton />;
  if (guide.data?.status !== 200) return null;
  const data = guide.data.data as unknown as Guide;
  const text = (value: Text | undefined) => localized(value, i18n.language);
  return (
    <GlassCard as="section" className="stack">
      <div className="row between">
        <h2>{t("ee.forms")}</h2>
        <Button variant="secondary" onClick={() => window.print()}>
          {t("ee.printForms")}
        </Button>
      </div>
      {data.table_status !== "verified" ? (
        <p className="ko">{t("ee.formsNotVerified", { version: data.table_version })}</p>
      ) : null}
      <ul>
        {data.general.map((line, index) => (
          <li key={index}>{text(line)}</li>
        ))}
      </ul>
      {data.forms.map((form) => (
        <details key={form.key} className="installment-row stack">
          <summary>
            <strong>{form.code}</strong> · {text(form.title)} · {form.members.map((m) => memberLabel(t, m)).join(", ")}
          </summary>
          <p className="muted">{text(form.where)}</p>
          {form.sections.map((section, index) => (
            <div key={index} className="stack">
              <h3>{text(section.title)}</h3>
              <ul className="plain-list">
                {section.fields.map((field, n) => (
                  <li key={n} className="stack">
                    <span>
                      <strong>{text(field.label)}</strong>
                      {field.critical ? <> <Badge tone="danger">{t("ee.critical")}</Badge></> : null}
                    </span>
                    {field.value !== null && field.value !== "" ? (
                      <span>
                        {t("ee.known")} : <strong>{String(field.value)}</strong>
                      </span>
                    ) : null}
                    <span className="muted">{text(field.guidance)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </details>
      ))}
      <a className="link" href={data.table_source} target="_blank" rel="noreferrer">
        {t("ee.officialSource")}
      </a>
    </GlassCard>
  );
}

interface StreamCheck {
  criterion: string;
  expected: number | boolean | null;
  result: "ok" | "ko" | "unknown";
}
interface Stream {
  key: string;
  province: string;
  name: Text;
  mode: Text;
  source_url: string;
  checks: StreamCheck[];
  outcome: "ok" | "ko" | "unknown";
}
interface DrawRow {
  held_on: string;
  number: number | null;
  category: string;
  label: Text;
  invitations: number;
  cutoff: number;
  gap: number;
}
const INTEREST_STATUSES = ["to_prepare", "submitted", "invited", "nominated", "refused", "expired"] as const;
const OUTCOME_TONE = { ok: "success", unknown: "default", ko: "danger" } as const;

/** EE-7: provincial streams (criteria ok / ko / unknown), expressions of interest, latest draws. */
function Provinces({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshDossier();
  const provinces = useImmigrationDossiersProvincesRetrieve(id, { query: { staleTime: 0 } });
  const draws = useImmigrationDossiersDrawsRetrieve(id, { query: { staleTime: 0 } });
  const interests = useImmigrationInterestsList({ dossier: id });
  const create = useImmigrationInterestsCreate({ mutation: { onSuccess: refresh } });
  const update = useImmigrationInterestsPartialUpdate({ mutation: { onSuccess: refresh } });
  const text = (value: Text | undefined) => localized(value, i18n.language);
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  const streams = provinces.data?.status === 200 ? ((provinces.data.data as unknown as { streams: Stream[] }).streams ?? []) : [];
  const drawData = draws.data?.status === 200 ? (draws.data.data as unknown as { score: number; draws: DrawRow[] }) : null;
  const tracked = new Map((interests.data?.status === 200 ? interests.data.data.results : []).map((i) => [i.stream_key, i]));
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("ee.provinces")}</h2>
      <p className="muted">{t("ee.provincesIntro")}</p>
      {provinces.isPending ? <Skeleton /> : null}
      <ul className="plain-list doc-grid">
        {streams.map((stream) => {
          const interest = tracked.get(stream.key);
          return (
            <li key={stream.key} className={`doc-item doc-item--${stream.outcome === "ok" ? "verified" : stream.outcome === "ko" ? "to_redo" : "to_request"} stack`}>
              <div className="row between">
                <strong>{text(stream.name)}</strong>
                <Badge tone={OUTCOME_TONE[stream.outcome]}>{t(`ee.outcome.${stream.outcome}`)}</Badge>
              </div>
              <span className="muted">{text(stream.mode)}</span>
              <ul>
                {stream.checks.map((check) => (
                  <li key={check.criterion} className={check.result === "ko" ? "ko" : check.result === "ok" ? "ok" : "muted"}>
                    {t(`ee.criteria.${check.criterion}`, { value: check.expected ?? "?" })} : {t(`ee.outcome.${check.result}`)}
                  </li>
                ))}
              </ul>
              <div className="row">
                {interest ? (
                  <SelectField
                    label={t("ee.interestStatus")}
                    value={interest.status ?? "to_prepare"}
                    options={INTEREST_STATUSES.map((x) => ({ value: x, label: t(`ee.interest.${x}`) }))}
                    onChange={(e) => update.mutate({ id: interest.id, data: { status: e.target.value as (typeof INTEREST_STATUSES)[number] } })}
                  />
                ) : (
                  <Button variant="secondary" disabled={create.isPending} onClick={() => create.mutate({ data: { dossier: id, stream_key: stream.key } })}>
                    {t("ee.trackInterest")}
                  </Button>
                )}
                <a className="link" href={stream.source_url} target="_blank" rel="noreferrer">
                  {t("ee.officialSource")}
                </a>
              </div>
            </li>
          );
        })}
      </ul>
      <h3>{t("ee.draws")}</h3>
      {drawData && drawData.draws.length ? (
        <ul className="plain-list">
          {drawData.draws.map((draw, index) => (
            <li key={index} className="row between">
              <span>
                {new Date(draw.held_on).toLocaleDateString(locale, { timeZone: "Africa/Douala" })} · {text(draw.label) || draw.category} ·{" "}
                {t("ee.cutoff", { cutoff: draw.cutoff, invitations: draw.invitations })}
              </span>
              <Badge tone={draw.gap >= 0 ? "success" : "default"}>{t("ee.gap", { gap: draw.gap > 0 ? `+${draw.gap}` : draw.gap })}</Badge>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t("ee.noDraws")}</p>
      )}
      <p className="muted">{t("ee.drawsNotice")}</p>
    </GlassCard>
  );
}

function Review({ dossier }: { dossier: Dossier }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshDossier();
  const checks = useImmigrationDossiersChecksRetrieve(dossier.id, { query: { staleTime: 0 } });
  const settings = useImmigrationSettingsList();
  const accept = useImmigrationDossiersAcceptNoticeCreate({ mutation: { onSuccess: refresh } });
  const validate = useImmigrationDossiersValidateCreate({ mutation: { onSuccess: refresh } });
  const submitted = useImmigrationDossiersSubmittedCreate({ mutation: { onSuccess: refresh } });
  const [reference, setReference] = useState("");
  const checklist = useImmigrationDossiersChecklistList(dossier.id);
  const titles = new Map(
    (checklist.data?.status === 200 ? checklist.data.data : []).map((i) => [i.rule_key, localized(i.title, i18n.language)]),
  );
  const issues = checks.data?.status === 200 ? ((checks.data.data as { issues: Json[] }).issues ?? []) : [];
  const notice = settings.data?.status === 200 ? settings.data.data.results[0]?.client_notice : undefined;
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  const date = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString(locale, { timeZone: "Africa/Douala" }) : "");
  const validateError = fieldErrors(validate.error);
  // 403 (not a designated validator) carries its reason in "detail", already translated.
  const validateMessage =
    validate.error instanceof ApiError ? (validate.error.detail ?? validateError.detail) : undefined;
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("ee.checks")}</h2>
      {issues.length ? (
        <ul>
          {issues.map((issue, index) => (
            <li key={index} className="ko">
              {t(`ee.issues.${String(issue.code)}`, {
                item: titles.get(String(issue.item ?? "")) ?? String(issue.item ?? ""),
                member: issue.member ? memberLabel(t, String(issue.member)) : "",
              })}
            </li>
          ))}
        </ul>
      ) : (
        <p className="ok">{t("ee.noIssue")}</p>
      )}
      <h3>{t("ee.notice")}</h3>
      <p className="bible-text">{localized(notice, i18n.language)}</p>
      {dossier.notice_accepted_at ? (
        <p className="ok">{t("ee.noticeAccepted", { date: date(dossier.notice_accepted_at) })}</p>
      ) : (
        <Button variant="secondary" disabled={accept.isPending} onClick={() => accept.mutate({ id: dossier.id })}>
          {t("ee.acceptNotice")}
        </Button>
      )}
      {dossier.validated_at ? (
        <p className="ok">{t("ee.validated", { date: date(dossier.validated_at) })}</p>
      ) : (
        <div className="stack">
          <Button disabled={validate.isPending} onClick={() => validate.mutate({ id: dossier.id })}>
            {t("ee.validate")}
          </Button>
          {validate.isError ? (
            <p role="alert" className="ko">
              {validateMessage ?? Object.values(validateError)[0] ?? t("common.error")}
            </p>
          ) : null}
        </div>
      )}
      <h3>{t("ee.submittedTitle")}</h3>
      {dossier.submitted_at ? (
        <p className="ok">{t("ee.submitted", { date: date(dossier.submitted_at), reference: dossier.ircc_reference })}</p>
      ) : (
        <div className="row">
          <TextField label={t("ee.irccReference")} value={reference} onChange={(e) => setReference(e.target.value)} />
          <Button
            variant="secondary"
            disabled={!dossier.validated_at || !reference.trim() || submitted.isPending}
            onClick={() => submitted.mutate({ id: dossier.id, data: { ircc_reference: reference } })}
          >
            {t("ee.markSubmitted")}
          </Button>
        </div>
      )}
    </GlassCard>
  );
}

/** The owner or the manager entrusts the file to a team member (never to the client). */
function Assignment({ dossier }: { dossier: Dossier }) {
  const { t } = useTranslation();
  const refresh = useRefreshDossier();
  const role = useCurrentRole();
  const members = useCrmMembersList();
  const update = useImmigrationDossiersPartialUpdate({ mutation: { onSuccess: refresh } });
  const team = (members.data?.status === 200 ? members.data.data : []).filter((m) =>
    ["owner", "manager", "advisor"].includes(m.role),
  );
  const current = team.find((m) => m.id === dossier.advisor);
  if (role !== "owner" && role !== "manager") {
    return current ? <p className="muted">{t("ee.assignedTo", { name: current.full_name })}</p> : null;
  }
  const errors = fieldErrors(update.error);
  return (
    <SelectField
      label={t("ee.assignee")}
      value={dossier.advisor ?? ""}
      error={errors.advisor}
      options={[{ value: "", label: "—" }, ...team.map((m) => ({ value: m.id, label: `${m.full_name} · ${t(`team.roles.${m.role}`)}` }))]}
      onChange={(e) => e.target.value && update.mutate({ id: dossier.id, data: { advisor: e.target.value } })}
    />
  );
}

/** D-077: the owner or the manager opens (or closes) the client's follow-up in the mobile app;
 * the team reads and answers the client's messages here. */
function ClientFollowUp({ dossier }: { dossier: Dossier }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshDossier();
  const role = useCurrentRole();
  const access = useImmigrationDossiersClientAccessRetrieve(dossier.id);
  const open = useImmigrationDossiersClientAccessCreate({ mutation: { onSuccess: refresh } });
  const close = useImmigrationDossiersClientAccessDestroy({ mutation: { onSuccess: refresh } });
  const messages = useImmigrationDossiersClientMessagesList(dossier.id, { query: { refetchInterval: 20_000 } });
  const reply = useImmigrationDossiersClientMessagesCreate({ mutation: { onSuccess: refresh } });
  const [email, setEmail] = useState("");
  const [body, setBody] = useState("");
  const state = access.data?.status === 200 ? access.data.data : undefined;
  const thread = messages.data?.status === 200 ? messages.data.data : [];
  const canManage = role === "owner" || role === "manager";
  const errors = fieldErrors(open.error);
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  const when = (iso: string) => new Date(iso).toLocaleString(locale, { timeZone: "Africa/Douala" });

  return (
    <GlassCard as="section" className="stack">
      <div className="row between">
        <h2>{t("ee.client.title")}</h2>
        <Badge tone={state?.open ? "success" : "default"}>{state?.open ? t("ee.client.open") : t("ee.client.closed")}</Badge>
      </div>
      <p className="muted">{t("ee.client.intro")}</p>
      {canManage ? (
        state?.open ? (
          <Button variant="secondary" disabled={close.isPending} onClick={() => close.mutate({ id: dossier.id })}>
            {t("ee.client.close")}
          </Button>
        ) : (
          <div className="row">
            <TextField
              label={t("ee.client.email")}
              type="email"
              value={email || state?.email || ""}
              error={errors.email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button disabled={open.isPending} onClick={() => open.mutate({ id: dossier.id, data: { email: email || state?.email || "" } })}>
              {t("ee.client.openAction")}
            </Button>
          </div>
        )
      ) : null}
      <h3>{t("ee.client.messages")}</h3>
      {thread.length ? (
        <ol className="messages client-thread">
          {thread.map((m) => (
            <li key={m.id} className={`bubble bubble--${m.from_client ? "in" : "out"}`}>
              <p className="bubble__body">{m.body}</p>
              <p className="bubble__meta">
                {m.author_name} · {when(m.created_at)}
              </p>
            </li>
          ))}
        </ol>
      ) : (
        <p className="muted">{t("ee.client.noMessage")}</p>
      )}
      {state?.open ? (
        <div className="row">
          <TextField label={t("ee.client.reply")} value={body} onChange={(e) => setBody(e.target.value)} />
          <Button
            disabled={!body.trim() || reply.isPending}
            onClick={() => reply.mutate({ id: dossier.id, data: { body } }, { onSuccess: () => setBody("") })}
          >
            {t("ee.client.send")}
          </Button>
        </div>
      ) : null}
    </GlassCard>
  );
}

const TABS = ["synthesis", "strategy", "profile", "documents", "forms", "provinces", "client", "officer", "review"] as const;
type Tab = (typeof TABS)[number];

/** Where the file stands, from the facts already recorded (nothing to tick by hand). */
function Progress({ dossier }: { dossier: Dossier }) {
  const { t } = useTranslation();
  const profile = (dossier.profile ?? {}) as Record<string, unknown>;
  const milestones = (profile.milestones ?? {}) as Record<string, unknown>;
  const principal = (profile.principal ?? {}) as Record<string, unknown>;
  const tested = Boolean((principal.first_language as Record<string, unknown> | undefined)?.test_date);
  const eca = Boolean((principal.eca as Record<string, unknown> | undefined)?.date);
  const steps = [
    { key: "assessment", done: dossier.status !== "profile" || (tested && eca) },
    { key: "tests", done: tested && eca },
    { key: "eeProfile", done: Boolean(milestones.ee_profile_date) },
    { key: "invitation", done: Boolean(milestones.ita_date) },
    { key: "validation", done: Boolean(dossier.validated_at) },
    { key: "submitted", done: Boolean(dossier.submitted_at) },
  ];
  const current = steps.findIndex((step) => !step.done);
  return (
    <ol className="stepper" aria-label={t("fsw.progress")}>
      {steps.map((step, index) => (
        <li
          key={step.key}
          className={`stepper__step${step.done ? " stepper__step--done" : index === current ? " stepper__step--current" : ""}`}
          aria-current={index === current ? "step" : undefined}
        >
          <span className="muted">{index + 1}</span>
          {t(`fsw.step.${step.key}`)}
        </li>
      ))}
    </ol>
  );
}

export function DossierPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const dossier = useImmigrationDossiersRetrieve(id);
  const [tab, setTab] = useState<Tab>("synthesis");
  if (dossier.isPending)
    return (
      <Page>
        <Skeleton style={{ height: 120 }} />
      </Page>
    );
  if (dossier.data?.status !== 200)
    return (
      <Page>
        <GlassCard role="alert">{t("common.error")}</GlassCard>
      </Page>
    );
  const d = dossier.data.data;
  return (
    <Page
      title={d.contact_name || d.contact_phone}
      description={
        <>
          <Link to="/dossiers" className="link">
            {t("ee.title")}
          </Link>{" "}
          · {d.reference} · {t("fsw.programName")}
        </>
      }
      actions={
        <>
          <Badge tone={d.status === "submitted" || d.status === "validated" ? "success" : "default"}>{t(`ee.status.${d.status}`)}</Badge>
          <Assignment dossier={d} />
        </>
      }
    >
      <GlassCard className="stack">
        <Progress dossier={d} />
      </GlassCard>
      <div className="tabs" role="tablist" aria-label={t("fsw.sections")}>
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`tab-${key}`}
            aria-selected={tab === key}
            aria-controls={`panel-${key}`}
            className="tabs__tab"
            onClick={() => setTab(key)}
          >
            {t(`fsw.tab.${key}`)}
            {key === "client" && d.unread_client_messages ? <Badge tone="offer">{d.unread_client_messages}</Badge> : null}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} className="stack">
        {tab === "synthesis" ? (
          <>
            <Synthesis id={d.id} />
            <WhatIf id={d.id} />
          </>
        ) : null}
        {tab === "strategy" ? <Strategy id={d.id} /> : null}
        {tab === "profile" ? <ProfileForm key={d.id} dossier={d} /> : null}
        {tab === "documents" ? <Checklist id={d.id} /> : null}
        {tab === "forms" ? <FormsGuide id={d.id} /> : null}
        {tab === "provinces" ? <Provinces id={d.id} /> : null}
        {tab === "client" ? <ClientFollowUp dossier={d} /> : null}
        {tab === "officer" ? <OfficerReview dossier={d} /> : null}
        {tab === "review" ? <Review dossier={d} /> : null}
      </div>
    </Page>
  );
}
